"use client";

import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { formatCurrencyEUR, normalizeLocale, withLang } from "@/lib/i18n";
import { useCartActions, useCartItem } from "@/lib/store/cart-store-provider";
import { generateCartItemId } from "@/lib/store/cart-store";

export interface StoreProduct {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  price: number;
  images?: { asset?: { url?: string | null } | null }[];
  stock?: number;
}

interface ProductCardProps {
  product: StoreProduct;
}

export function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCartActions();
  const searchParams = useSearchParams();
  const locale = normalizeLocale(searchParams.get("lang"));
  const inCart = useCartItem(product._id);
  const imageUrl = product.images?.[0]?.asset?.url ?? "";
  const isOutOfStock = (product.stock ?? 0) <= 0;
  const productHref = product.slug ? withLang(`/shop/${product.slug}`, locale) : null;

  return (
    <article className="group">
      {/* ── Image ── */}
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#F0EBE3]">
        {productHref ? (
          <Link href={productHref} className="block h-full" tabIndex={-1} aria-hidden="true">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={product.name}
                fill
                className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-[#B8AB96]">—</div>
            )}
            {/* Slide-up discover bar */}
            <div className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center bg-[#4F4A43]/90 py-4 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-y-0">
              <span className="text-[11px] font-semibold tracking-[0.24em] text-[#F8F6F1] uppercase">
                {locale === "pt-PT" ? "Descobrir" : "Discover"}
              </span>
            </div>
          </Link>
        ) : (
          imageUrl ? (
            <Image src={imageUrl} alt={product.name} fill className="object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#B8AB96]">—</div>
          )
        )}

        {isOutOfStock && (
          <div className="absolute left-3 top-3 bg-[#F0EBE3]/90 px-2.5 py-1 text-[10px] font-medium tracking-[0.16em] text-[#8E7F6D] uppercase">
            {locale === "pt-PT" ? "Esgotado" : "Sold out"}
          </div>
        )}
      </div>

      {/* ── Info ── */}
      <div className="pt-4">
        <div className="flex items-start justify-between gap-4">
          {productHref ? (
            <Link href={productHref}>
              <h3 className="text-[15px] font-normal leading-snug text-[#2B2824] transition-colors duration-200 hover:text-[#8E7F6D]">
                {product.name}
              </h3>
            </Link>
          ) : (
            <h3 className="text-[15px] font-normal leading-snug text-[#2B2824]">{product.name}</h3>
          )}
          <p className="shrink-0 text-sm font-medium tabular-nums text-[#4F4A43]">
            {formatCurrencyEUR(product.price, locale)}
          </p>
        </div>

        {/* Quick add — only for products without options */}
        {!isOutOfStock && (
          <button
            onClick={() =>
              addItem(
                {
                  cartItemId: generateCartItemId(product._id, []),
                  productId: product._id,
                  name: product.name,
                  basePrice: product.price,
                  price: product.price,
                  image: imageUrl,
                  selectedOptions: [],
                },
                1,
              )
            }
            className="mt-3 text-[11px] font-medium tracking-[0.16em] text-[#8E7F6D] uppercase underline-offset-4 transition hover:text-[#2B2824] hover:underline"
          >
            {inCart
              ? locale === "pt-PT" ? "+ Adicionar" : "+ Add one more"
              : locale === "pt-PT" ? "Adicionar ao carrinho" : "Add to bag"}
          </button>
        )}
      </div>
    </article>
  );
}
